import React, { Component } from 'react'
import PropTypes from 'prop-types'
import pickby from 'lodash.pickby'
import pick from 'lodash.pick'
import identity from 'lodash.identity'
import isEmpty from 'lodash.isempty'
import { CookiesProvider } from 'react-cookie'
import Cookies from 'universal-cookie'
import { parse } from 'query-string'
import { v4 as uuid } from 'uuid'
import config from 'config'

import { isClient } from '@cred/shared/utils'
import { ThemeContext } from '@cred/react-theme'
import Snowplow from '@cred/react-analytics/Snowplow'
import { populatedGtmDataLayer } from '@cred/react-analytics/utils/data'
import gtm from '@cred/shared/gtm'
import { createLogger, logger } from '@cred/fe-logger'

import ClientLibrary from 'utils/client'
import ClientInfoService from 'utils/info-service'
import ApplicationErrorBoundary from 'components/error-boundary'
import { extractHostname } from 'utils/urls'
import { removeEmptyValues } from 'utils/data'
import getInsurancePrefillData from 'utils/getInsurancePrefillData'
import WidgetContext from 'components/widget-context'
import InitialValuesContext from 'components/initial-values-context'
import {
  DEFAULT_ACTIVE_TAB,
  INSURANCE_FORM_INITIAL_VALUES_FIELDS,
  YOUNG_ALFRED_STORAGE_KEY,
} from 'common/constants'

import getPolicyType from './utils/getPolicyType'
import styles from './WidgetApp.module.scss'
import 'purecss/build/pure.css' /* Importing Pure here otherwise the import order gets incorrect. */
import 'purecss/build/grids-responsive.css'
// eslint-disable-next-line import/extensions
import 'iframe-resizer/js/iframeResizer.contentWindow.min.js'
import '@cred/global-assets/styles/common.scss'
import '@cred/global-assets/styles/cred-icon.scss'

/**
  All widgets must have the same url structure
  /partners-widgets/:marketplace/:widgetType/

  (React Router is not being used here on purpose,
  now each widget has it own entry point)
*/
function getRouteParams(pathname) {
  const parts = pathname.split('/').filter(Boolean)

  if (parts.length < 2) {
    return {}
  }

  return {
    marketplace: parts[parts.length - 2],
    widgetType: parts[parts.length - 1],
  }
}

function getQueryParams(search) {
  return parse(search)
}

function getMarketplaceParam(marketplace, marketplaces) {
  const isMultiVertical = marketplace === 'multi-vertical'

  return isMultiVertical ? marketplaces.split(',') : [marketplace]
}

function parseMultiVerticalOptions(options) {
  try {
    return JSON.parse(options)
  } catch (_e) {
    return {}
  }
}

class App extends Component {
  constructor(props) {
    super(props)

    this.params = getRouteParams(window.location.pathname)
    this.query = getQueryParams(window.location.search)

    const {
      meta_contentId: contentId,
      meta_articleTitle: articleTitle,
      meta_articleTags: articleTags,
      meta_segmentId: segmentId,
      meta_campaignCode: campaignCode,
      meta_contentDistributor: contentDistributor,
      meta_contentCategory: contentCategory,
      meta_campaignCodeExt: campaignCodeExt,
      theme,
      variation,
      customCTA,
      pageUrl,
      marketplaces,
      defaultFilters,
      zipCode,
      activeTab,
      loanType,
      terms,
      sorting,
      omitUtmParams,
      multiVerticalOptions,
      isJumbo,
      ...options
    } = this.query

    const { marketplace, widgetType } = this.params

    // Initialize client library communication channel
    this.iframeId = isClient() ? window.frameElement?.id : null
    this.clientLibrary = new ClientLibrary(this.iframeId)

    // Initialize client information service
    this.clientInfoService = new ClientInfoService(this.clientLibrary)

    // Initialize the Cookies
    this.cookies = new Cookies()

    this.credclid = this.query.credclid || uuid()

    // Push Segemnt needed variables to GTM Data Layer
    populatedGtmDataLayer({
      contentId,
      articleTitle,
      articleTags,
      campaignCode,
      campaignCodeExt,
      contentDistributor,
      contentCategory,
      segmentId,
      pageUrl,
    })

    // Initialize GTM service
    gtm.initialize(config.gtmContainerId, this.cookies)

    // Initialize the logger component
    createLogger(
      {
        app: 'partners-widgets',
        credclid: this.credclid,
      },
      { credibleRoot: config.credibleRoot },
    )

    this.state = {
      // Options provided by partner.
      // These fallback values we need for:
      // - test widget in QA environment when host page is missing;
      // - local development.
      // Once widget is embedded into partner site these options will be replaced
      // by partners specific.
      options: {
        ui: {
          theme,
        },
        product: {
          type: widgetType,
          marketplace: getMarketplaceParam(marketplace, marketplaces),
          variation,
          customCTA,
        },
        meta: {
          contentId,
          articleTitle,
          articleTags,
          segmentId,
          campaignCode,
          contentDistributor,
          contentCategory,
          campaignCodeExt,
        },
        multiVertical: parseMultiVerticalOptions(multiVerticalOptions),
        omitUtmParams,
      },
      isJumbo,
      activeTab: activeTab || DEFAULT_ACTIVE_TAB,
      initialValues: {
        zipCode,
        loanType,
        terms,
        sorting,
        options,
      },
      // Global Snowplow context provided by Credible SDK.
      parentPage: {
        partnerReferrer: pageUrl || document.referrer || 'undefined',
        partnerDomain: extractHostname(document.referrer),
        partnerURL: pageUrl || document.referrer,
      },
      credclid: this.credclid,
      renderId: uuid(),
      source: this.query.source,
      campaignFromPageUrl: pageUrl,
    }
  }

  async componentDidMount() {
    // Analytics initialization
    Snowplow.initialize()

    await this.setWidgetHelpers()
    await this.setOptionsSuppliedByPartner()
    await this.setSnowplowContext()

    this.scrollToTop()
    this.setInsuranceFormInitialValues()

    this.clientLibrary.sendMessage('onInit')
  }

  componentWillUnmount() {
    this.clientLibrary.sendMessage('onDestroy')
  }

  async setWidgetHelpers() {
    const widgetHelpers = await this.clientInfoService.widgetHelpersDeferred

    console.log('[Partners widget] - received widget helpers from SDK', widgetHelpers)
    this.handleInitError(widgetHelpers)
    this.sendInfoLog(widgetHelpers)
    this.setCssVars(widgetHelpers, logger)
  }

  async setOptionsSuppliedByPartner() {
    const options = await this.clientInfoService.optionsDeferred

    this.setState({
      options,
    })
  }

  setCssVars = (widgetHelpers, loggerInstance) => {
    const { partnersCSSVars } = widgetHelpers || {}

    if (!partnersCSSVars || isEmpty(partnersCSSVars)) {
      return
    }

    try {
      Object.entries(partnersCSSVars).forEach(([key, value]) => {
        document.documentElement.style.setProperty(key, value)
      })
    } catch (e) {
      loggerInstance.error(
        'Credible_Widget_CSS_Variable_Setup_Error',
        widgetHelpers,
        'Error while setting CSS vars the widget',
      )
    }
  }

  async setSnowplowContext() {
    const { parentPage: parentPageDefault } = this.state
    const data = await this.clientInfoService.snowplowContextDeferred

    const parentPageNewValues = removeEmptyValues(data)

    this.setState({
      parentPage: { ...parentPageDefault, ...parentPageNewValues },
    })
  }

  setInsuranceFormInitialValues = () => {
    if (!isClient()) return

    const bowtieWidgetsInitialValues = pick(this.query, INSURANCE_FORM_INITIAL_VALUES_FIELDS)
    const { policyType } = this.query
    const prefill = getInsurancePrefillData()

    window.localStorage.setItem(
      YOUNG_ALFRED_STORAGE_KEY,
      JSON.stringify({
        application: {
          start: {
            ...bowtieWidgetsInitialValues,
            policyType: getPolicyType(policyType),
            ...prefill, // don't override existing data
          },
        },
      }),
    )
  }

  sendInfoLog = ({ widgetLoadTime, initWidgetOptions }) => {
    const { product } = initWidgetOptions || {}
    logger.info('widget_load_time_info', {
      widgetLoadTime,
      product,
    })
  }

  handleInitError = (widgetHelpers) => {
    const { initWidgetError } = widgetHelpers || {}

    if (initWidgetError) {
      logger.error('SDK_init_widget_error', widgetHelpers, 'Error while initializing the widget')
    }
  }

  scrollToTop() {
    // Request client library perform scroll
    this.clientLibrary.sendMessage('scrollRequest', 'top')
  }

  render() {
    const {
      options,
      parentPage,
      credclid,
      renderId,
      source,
      campaignFromPageUrl,
      initialValues,
      isJumbo,
      activeTab,
    } = this.state
    const theme = options.ui.theme === 'fox' ? 'fox1' : options.ui.theme
    const { children } = this.props

    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const widgetContext = { options, parentPage, credclid, renderId, source, campaignFromPageUrl }
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const initialValuesContext = {
      isJumbo,
      initialValues: pickby(initialValues, identity),
      activeTab,
    }

    return (
      <div className={styles.container}>
        <ApplicationErrorBoundary
          clientInfoService={this.clientInfoService}
          clientLibrary={this.clientLibrary}
        >
          <CookiesProvider cookies={this.cookies}>
            <WidgetContext.Provider value={widgetContext}>
              <InitialValuesContext.Provider value={initialValuesContext}>
                <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>
              </InitialValuesContext.Provider>
            </WidgetContext.Provider>
          </CookiesProvider>
        </ApplicationErrorBoundary>
      </div>
    )
  }
}

App.propTypes = {
  children: PropTypes.node.isRequired,
}

export { getRouteParams, getQueryParams, parseMultiVerticalOptions }

export default App
